import cx from 'classnames';
import { useCallback, useEffect, useState } from "react"
import { useDropzone } from "react-dropzone"
import { core } from "etl/core";
import { Button } from "../Button"
import { Icon, IconName, IconSize } from '../Icon';
import { FileSelectorDocumentProps, defaultProps } from "./FileSelector.type";
import styles from "./FileSelector.module.scss"

export const FileSelector = (props: FileSelectorDocumentProps) => {
	const { onUpload, uploaded, clearFile, uploading, ...attributes } = { ...defaultProps, ...props };

	const { displayErrorToast } = core.useUiUseCases();

	const [formData, setFormData] = useState<FormData>();
	const [file, setFile] = useState<File>();

	useEffect(() => {
		setFormData(undefined);
		setFile(undefined);
	}, [clearFile])

	const uploadDocument = useCallback(async (): Promise<void> => {
		if (!formData) return;
		const result = await onUpload(formData);
		if (result) setFormData(undefined);
	}, [formData, onUpload]);

	const importFile = useCallback((file: File): Promise<void> => new Promise((resolve, reject) => {
		try {
			const formData = new FormData();
			formData.append("files", file);
			setFormData(formData);
			setFile(file);
			resolve();
		} catch (err) {
			console.error(err);
			displayErrorToast({ message: "Erreur lors de l'import du fichier" });
			reject();
		}
	}), [displayErrorToast])

	const onDrop = useCallback((selectedFiles: File[]) => {
		for (const file of selectedFiles) importFile(file);
	}, [importFile])

	const onUploadHandler = useCallback((e: React.MouseEvent<HTMLElement, MouseEvent>) => {
		e.stopPropagation();
		uploadDocument();
	}, [uploadDocument])

	const {
		getRootProps,
		getInputProps,
		isDragActive
	} = useDropzone({
		onDrop
	})

	const getCurrentIcon = () => {
		if (uploading) return IconName.Loading;
		if (uploaded) return IconName.Done;
		return IconName.Upload;
	}

	return (
		<>
			<div
				{...attributes}
				className={cx(
					styles.FileSelector,
					uploaded && styles.Done
				)}
				{...getRootProps()}
			>
				<input {...getInputProps()} />
				<Icon
					name={getCurrentIcon()}
					className={styles.FileSelectorIcon}
					iconSize={IconSize.Large}
				/>
				{
					(!formData && !uploaded && !uploading &&
						(isDragActive ?
							<p>Déposer le fichier ici ...</p> :
							<p>Glisser-déposer ou cliquer pour sélectionner un fichier</p>
						)
					)
				}
				{!uploading && uploaded && !formData && (
					<p>Fichier téléversé</p>
				)}
				{uploading && (
					<p>Téléversement en cours</p>
				)}
				{file && (
					<p>Fichier sélectionné : {file.name}</p>
				)}
				{!uploading && formData && (
					<div className={styles.FileSelectorFooter} onClick={e => e.stopPropagation()}>
						<Button onClick={(e) => onUploadHandler(e)}>Téléverser</Button>
					</div>
				)}
			</div>
		</>
	)
}
